import React, { useState, type ChangeEvent, type FormEvent, useRef } from "react";
import { toast } from "react-toastify";
import { sendContactForm } from "@/api/contactService";
import { Upload, X, FileText } from "lucide-react";
import { useDashboardTheme } from "@/pages/MeetingDashboard/themeHelper";

interface FormDataState {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  message: string;
}

interface FormErrors {
  [key: string]: string;
}

const ContactForm: React.FC = () => {
  const { isDark, colors } = useDashboardTheme();
  const [formData, setFormData] = useState<FormDataState>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    message: "",
  });

  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const cleanedValue = value.replace(/^\s+/, ""); // Remove leading spaces
    setFormData((prev) => ({ ...prev, [name]: cleanedValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error("File size must be less than 10MB");
        return;
      }
      
      setFile(selectedFile);
      
      // Create preview if it's an image
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result as string);
        };
        reader.readAsDataURL(selectedFile);
      } else {
        setFilePreview(null);
      }
    }
  };

  const clearFile = () => {
    setFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validate = (data: FormDataState = formData): FormErrors => {
    const newErrors: FormErrors = {};
    const nameRegex = /^[A-Za-z\s]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[1-9]\d{9,14}$/; // US and international (including South America) 10-15 digits

    // First Name
    if (!data.firstName.trim()) {
      newErrors.firstName = "First Name is required.";
    } else if (data.firstName.trim().length < 2) {
      newErrors.firstName = "First Name must be at least 2 characters.";
    } else if (!nameRegex.test(data.firstName.trim())) {
      newErrors.firstName = "First Name can contain only letters and spaces.";
    }

    // Last Name
    if (!data.lastName.trim()) {
      newErrors.lastName = "Last Name is required.";
    } else if (data.lastName.trim().length < 2) {
      newErrors.lastName = "Last Name must be at least 2 characters.";
    } else if (!nameRegex.test(data.lastName.trim())) {
      newErrors.lastName = "Last Name can contain only letters and spaces.";
    }

    // Email
    if (!data.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!emailRegex.test(data.email.trim())) {
      newErrors.email = "Enter a valid email address.";
    }

    // Phone Number (Optional now)
    if (data.phoneNumber.trim() && !phoneRegex.test(data.phoneNumber.trim())) {
      newErrors.phoneNumber = "Enter a valid US or international phone number (e.g. +1234567890).";
    }

    // Message
    if (!data.message.trim()) {
      newErrors.message = "Message is required.";
    } else if (data.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters.";
    }

    return newErrors;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const cleanedData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [key, value.trim()]),
    ) as FormDataState;

    setFormData(cleanedData);

    const validationErrors = validate(cleanedData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("firstName", cleanedData.firstName);
      payload.append("lastName", cleanedData.lastName);
      payload.append("email", cleanedData.email);
      if (cleanedData.phoneNumber) {
        payload.append("phoneNumber", cleanedData.phoneNumber);
      }
      payload.append("message", cleanedData.message);
      
      if (file) {
        payload.append("files", file);
      }

      await sendContactForm(payload);
      toast.success("Message sent successfully!");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        message: "",
      });
      clearFile();
    } catch (error: unknown) {
      const errMsg =
        typeof error === "object" && error !== null && "message" in error
          ? String((error as { message: string }).message)
          : "Something went wrong. Try again later.";
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const accent = isDark ? "#B8F890" : "#5a9d50";

  return (
    <div className={isDark ? "bg-[#050505] min-h-screen text-white font-sans" : "min-h-screen font-sans"} style={{ background: colors.bg, color: colors.text }}>
      <div className="flex flex-col items-center justify-center px-4 py-16">
        
        {/* Header Section */}
        <div className="text-center max-w-2xl mb-12">
          <h1
            className="text-4xl font-bold tracking-tight mb-4 bg-clip-text text-transparent"
            style={{
              backgroundImage: isDark
                ? "linear-gradient(to right, #c084fc, #B8F890)"
                : "linear-gradient(to right, #7e22ce, #5a9d50)",
            }}
          >
            Get in Touch
          </h1>
          <p style={{ color: colors.textMuted }} className="text-lg">
            Have a question, partnership idea, or need support? We'd love to hear from you. 
            Fill out the form below and our team will get back to you shortly.
          </p>
        </div>

        <form
          className="w-full max-w-4xl backdrop-blur-sm rounded-3xl p-8 md:p-10 shadow-2xl"
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            color: colors.text,
          }}
          onSubmit={handleSubmit}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* First Name */}
            <div className="group">
              <label className="block mb-2 text-sm font-medium transition-colors" style={{ color: colors.text }}>
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full p-3.5 rounded-xl transition-all focus:outline-none"
                style={{
                  background: colors.inputBg,
                  border: `1px solid ${colors.inputBorder}`,
                  color: colors.inputText,
                }}
                placeholder="Enter your first name"
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm mt-1.5">{errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div className="group">
              <label className="block mb-2 text-sm font-medium transition-colors" style={{ color: colors.text }}>
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full p-3.5 rounded-xl transition-all focus:outline-none"
                style={{
                  background: colors.inputBg,
                  border: `1px solid ${colors.inputBorder}`,
                  color: colors.inputText,
                }}
                placeholder="Enter your last name"
              />
              {errors.lastName && (
                <p className="text-red-500 text-sm mt-1.5">{errors.lastName}</p>
              )}
            </div>

            {/* Email */}
            <div className="group">
              <label className="block mb-2 text-sm font-medium transition-colors" style={{ color: colors.text }}>
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-3.5 rounded-xl transition-all focus:outline-none"
                style={{
                  background: colors.inputBg,
                  border: `1px solid ${colors.inputBorder}`,
                  color: colors.inputText,
                }}
                placeholder="Enter email"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1.5">{errors.email}</p>
              )}
            </div>

            {/* Phone Number (Optional) */}
            <div className="group">
              <label className="block mb-2 text-sm font-medium transition-colors" style={{ color: colors.text }}>
                Phone Number <span style={{ color: colors.textMuted }} className="text-xs font-normal ml-1">(Optional)</span>
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                onInput={(e) => {
                  const input = e.currentTarget;
                  // Allow '+' at the beginning, followed by digits
                  input.value = input.value.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, ""); 
                }}
                className="w-full p-3.5 rounded-xl transition-all focus:outline-none"
                style={{
                  background: colors.inputBg,
                  border: `1px solid ${colors.inputBorder}`,
                  color: colors.inputText,
                }}
                placeholder="e.g. +12345678900"
              />
              {errors.phoneNumber && (
                <p className="text-red-500 text-sm mt-1.5">{errors.phoneNumber}</p>
              )}
            </div>
          </div>

          {/* Message */}
          <div className="mt-6 group">
            <label className="block mb-2 text-sm font-medium transition-colors" style={{ color: colors.text }}>
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="w-full p-4 rounded-xl min-h-[140px] transition-all resize-y focus:outline-none"
              style={{
                background: colors.inputBg,
                border: `1px solid ${colors.inputBorder}`,
                color: colors.inputText,
              }}
              placeholder="How can we help you?"
            />
            {errors.message && (
              <p className="text-red-500 text-sm mt-1.5">{errors.message}</p>
            )}
          </div>

          {/* File Upload / Attachment Section */}
          <div className="mt-6">
            <label className="block mb-2 text-sm font-medium" style={{ color: colors.text }}>
              Attachment <span style={{ color: colors.textMuted }} className="text-xs font-normal ml-1">(Optional)</span>
            </label>
            
            {!file ? (
              <div 
                className="w-full border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer transition-colors"
                style={{
                  background: colors.surfaceAlt,
                  borderColor: colors.border,
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="p-3 rounded-full mb-3" style={{ background: `${accent}1f` }}>
                  <Upload className="w-6 h-6" style={{ color: accent }} />
                </div>
                <p className="text-sm font-medium mb-1" style={{ color: colors.text }}>Click to upload a file or drag and drop</p>
                <p className="text-xs" style={{ color: colors.textMuted }}>SVG, PNG, JPG, PDF or TXT (max. 10MB)</p>
              </div>
            ) : (
              <div className="w-full border rounded-xl p-4 flex items-center justify-between" style={{ background: colors.surfaceAlt, borderColor: colors.border }}>
                <div className="flex items-center space-x-4 overflow-hidden">
                  <div className="shrink-0 w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center border" style={{ background: colors.surface, borderColor: colors.border }}>
                    {filePreview ? (
                      <img src={filePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <FileText className="w-6 h-6" style={{ color: colors.textMuted }} />
                    )}
                  </div>
                  <div className="truncate">
                    <p className="text-sm font-medium truncate max-w-[200px] sm:max-w-xs" style={{ color: colors.text }}>{file.name}</p>
                    <p className="text-xs" style={{ color: colors.textMuted }}>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearFile}
                  className="p-2 hover:bg-red-500/10 rounded-full text-red-500 transition-colors"
                  title="Remove file"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.txt"
            />
          </div>

          {/* Submit Action */}
          <div className="mt-10 flex items-center justify-end border-t pt-6" style={{ borderColor: colors.border }}>
            <button
              type="submit"
              disabled={submitting}
              className="relative overflow-hidden group px-10 py-3.5 rounded-xl font-bold shadow-lg transition-all transform hover:-translate-y-0.5 active:translate-y-0"
              style={{
                background: isDark
                  ? "linear-gradient(to right, #B8F890, #9AEB6B)"
                  : "linear-gradient(to right, #5a9d50, #4c8a43)",
                color: isDark ? "#000000" : "#ffffff",
              }}
            >
              <span className="relative z-10 flex items-center justify-center">
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5" style={{ color: isDark ? "#000000" : "#ffffff" }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending Message...
                  </>
                ) : (
                  "Send Message"
                )}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
